import { useState, useEffect } from 'react';

export default function ImageRegenPanel({ regen, onAccept, onCancel, onRetry }) {
  const [editedPrompt, setEditedPrompt] = useState('');

  // Reset prompt only when a brand new image starts regenerating
  useEffect(() => {
    setEditedPrompt(regen?.promptHint || '');
  }, [regen?.originalUrl]);

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel]);

  if (!regen) return null;

  const { status, originalUrl, newUrl, error } = regen;

  return (
    <>
      <div className="irp-backdrop" onClick={onCancel} aria-hidden="true" />

      <div className="irp-panel" role="dialog" aria-modal="true" aria-label="Regenerate image">

        {/* Header */}
        <div className="irp-header">
          <span className="irp-title">
            { status === 'idle'     && 'Regenerate image' }
{ status === 'loading'  && 'Generating new image…' }
{ status === 'preview'  && 'Preview — keep or retry?' }
{ status === 'error'    && 'Generation failed' }
{ status === 'accepting'&& 'Saving…' }
          </span>
          <button className="irp-close" onClick={onCancel} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Editable prompt */}
        <div className="irp-prompt-row">
          <label className="irp-prompt-label" htmlFor="irp-prompt">Prompt</label>
          <div className="irp-prompt-input-row">
            <textarea
              id="irp-prompt"
              className="irp-prompt-textarea"
              value={editedPrompt}
              onChange={e => setEditedPrompt(e.target.value)}
              rows={2}
              placeholder="Describe the image you want…"
              disabled={status === 'accepting' || status === 'loading'}
            />
            {/* Re-run button with edited prompt */}
            {(status === 'preview' || status === 'error') && (
              <button
                className="irp-btn irp-btn--regen-icon"
                onClick={() => onRetry(editedPrompt)}
                title="Regenerate with this prompt"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Side-by-side images */}
        <div className="irp-images">
  <div className="irp-img-col">
    <span className="irp-img-label">Current</span>
    <img
  src={originalUrl.startsWith('http') ? originalUrl : `${process.env.REACT_APP_API_URL || ''}${originalUrl}`}
  alt="Current"
  className="irp-img irp-img--old"
/>
  </div>

  {status !== 'idle' && (
    <div className="irp-img-col">
      <span className="irp-img-label">
        {status === 'loading' ? 'Generating…' : status === 'error' ? 'Failed' : 'New'}
      </span>

      {status === 'loading' && (
        <div className="irp-skeleton">
          <div className="irp-skeleton-shimmer" />
        </div>
      )}

      {status === 'error' && (
        <div className="irp-error-box">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p>{error}</p>
        </div>
      )}

      {(status === 'preview' || status === 'accepting') && newUrl && (
        <img
    src={`${newUrl.startsWith('http') ? newUrl : (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '') + newUrl}?t=${Date.now()}`}
    alt="New version"
    className="irp-img irp-img--new"
  />
      )}
    </div>
  )}
</div>

        {/* Action buttons */}
        <div className="irp-actions">
  {status === 'idle' && (
    <button className="irp-btn irp-btn--accept" onClick={() => onRetry(editedPrompt)}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
      Generate
    </button>
  )}

  {status === 'preview' && (<>
    <button className="irp-btn irp-btn--accept" onClick={onAccept}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
      Use this image
    </button>
    <button className="irp-btn irp-btn--retry" onClick={() => onRetry(editedPrompt)}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
      Try again
    </button>
    <button className="irp-btn irp-btn--cancel" onClick={onCancel}>Keep original</button>
  </>)}

  {status === 'loading' && (
    <button className="irp-btn irp-btn--cancel" onClick={onCancel}>Cancel</button>
  )}

  {status === 'error' && (<>
    <button className="irp-btn irp-btn--retry" onClick={() => onRetry(editedPrompt)}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
      Retry
    </button>
    <button className="irp-btn irp-btn--cancel" onClick={onCancel}>Cancel</button>
  </>)}

  {status === 'accepting' && (
    <span className="irp-saving">
      <span className="irp-dots"><span/><span/><span/></span>
      Saving to lesson…
    </span>
  )}
</div>
      </div>
    </>
  );
}
